import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "flowbite-react";
import { useGetData } from "../../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechaFormatosGeneralesInterface } from "../../../../../interfaces/FechaFormatosGeneralesInterface";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { AutorizacionProporcionarDocumentosListaPrint } from "./AutorizacionProporcionarDocumentosListaPrint";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const AutorizacionProporcionarDocumentosListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario =
    "AUTORIZACIÓN PARA PROPORCIONAR DOCUMENTOS E INFORMACIÓN";
  const [formData, setFormData] = useState({
    date: "",
  });

  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { reset } = useForm();

  const queryFechaFG = useGetData<FechaFormatosGeneralesInterface>({
    url: `/fecha_formatosgenerales/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "fecha_formatosgenerales"],
  });

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (queryFechaFG.data) {
      const fechafg = queryFechaFG.data;
      reset({
        fecha_requisitos_pruebas: fechafg.fecha_requisitos_pruebas || "",
      });
    }
  }, [queryFechaFG.data, reset]);

  const navigate = useNavigate();

  const handlePrint = () => {
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden mt-4 mb-4 relative ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        {/* Header */}
        <Header titulo={tituloFormulario} />

        {/* Contenido del formulario */}
        <div className="p-6">
          <div className="space-y-6">
            <p className={`text-justify leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              <span className="mr-2">
                Bajo protesta de decir verdad hago constar que los datos
                proporcionados en el proceso de evaluación son verdaderos.
              </span>
            </p>

            <p className={`text-justify ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              Asimismo; autorizo a los{" "}
              <span>
                {queryFechaFG.data?.fecha_autorizacion_proporcionar
                  ? (() => {
                      const date = new Date(
                        queryFechaFG.data.fecha_autorizacion_proporcionar
                      );
                      date.setDate(date.getDate() + 1);
                      return date.toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      });
                    })()
                  : "Fecha no disponible"}
              </span>{" "}
              a la empresa SKA TRACKING AND SECURITY S.A. de C.V. para que
              proporcione toda la documentación e información derivada de mis
              evaluaciones.
            </p>

            <p className={`text-justify ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>
              Se firma la presente por triplicado para los efectos legales
              conducentes
            </p>
          </div>
          <br />
          <br />
          <div className={`text-left mb-8 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
            <h2 className="text-xl font-bold mb-2 flex items-left">
              {`NOMBRE: ${queryEvaluado.data?.nombre || ""} ${
                queryEvaluado.data?.apellido_paterno || ""
              } ${queryEvaluado.data?.apellido_materno || ""}`}
            </h2>
            <h2 className="text-xl font-bold mb-2 flex items-left">
              {`PUESTO: ${queryEvaluado.data?.puesto || ""}`}
            </h2>
            <h2 className="text-xl font-bold mb-2 flex items-left">
              {`FIRMA: `}
            </h2>
          </div>
        </div>

        {/* Botones */}
        <div className="mt-4 mb-4 px-4">
          <div className="flex justify-between">
            <Button
              color="red"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(-1);
              }}
              className={`bg-red-500 hover:bg-red-600 text-white ${isDarkMode ? "bg-red-600 hover:bg-red-700" : ""}`}
            >
              Regresar
            </Button>
            <Button
              color="blue"
              className={`z-10 bg-blue-500 hover:bg-blue-600 text-white ${isDarkMode ? "bg-blue-600 hover:bg-blue-700" : ""}`}
              onClick={handlePrint}
            >
              Imprimir
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className={`h-12 mt-8 flex items-center justify-center ${isDarkMode ? "bg-blue-800" : "bg-customBlue"}`}>
          <p className="text-sm text-white px-4">
            <span className="font-bold">DOCUMENTO CONFIDENCIAL</span>, prohibido
            su reproducción, copia o sacar este documento de las Instalaciones de
            Skyangel
          </p>
        </div>
        {/* Imprimir */}
        <div id="printableContent" style={{ display: "none" }}>
          <AutorizacionProporcionarDocumentosListaPrint />
        </div>
      </div>
    </div>
  );
};
